import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { FileUp, ListChecks, Minus, Scissors, Upload } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import { odkazNaNastavenia } from "@/components/nastavenia/kategorie";
import { OpravyZoznam } from "@/components/import/OpravyZoznam";
import {
  TabulkaHran,
  type PriradenaDoska,
} from "@/components/import/TabulkaHran";
import type { Predvyplnenie } from "@/components/cennik/MaterialEditDialog";
import {
  TabulkaPoloziek,
  jeVyriesena,
  klucPolozky,
} from "@/components/import/TabulkaPoloziek";
import { VysvetlenieZapamatania } from "@/components/import/Zapamatanie";
import {
  PRAZDNY_STAV as PRAZDNY,
  type StavPolozky,
} from "@/components/import/VyberPolozkyDialog";
import { DRUHY_CISLA, type DruhCisla } from "@/lib/druhCisla";
import {
  VyberZakazky,
  type ZakazkaNaPripojenie,
} from "@/components/zakazka/PripojitDialog";
import { useDebounce } from "@/lib/useDebounce";
import { type Krok, opravyPreKrok } from "@/lib/opravyKroky";
import { cn } from "@/lib/utils";
import type { DruhMaterialu, Sekcia } from "@/api/schemas";
import {
  useTopsolidAnalyza,
  useTopsolidPotvrdit,
  useTopsolidPridajCast,
  type Delenie,
  type DielecNahlad,
  type MapovaniePolozka,
  type PrirezNahlad,
  type Materialy,
  type PracovneDosky,
  type Rozhodnutie,
  type TopsolidAnalyza,
  type Zaradenie,
} from "@/api/hooks/useTopsolidRaw";
import {
  stiahniKonceptSubor,
  useKoncepty,
  useOznacVideny,
  useUlozKoncept,
  useZalozKoncept,
  useZmazKoncept,
  type Koncept,
  type KonceptStav,
} from "@/api/hooks/useTopsolidKoncept";
import {
  useUpdateNahlad,
  useUpdateVykonat,
  type KovanieVolby,
  type UpdateNahlad,
} from "@/api/hooks/useTopsolidUpdate";
import { UpdateZmenyDialog } from "@/components/import/UpdateZmenyDialog";
import { druhAktualizovany } from "@/lib/import/druhVarianty";

/**
 * Import surového TopSolid exportu s mapovacou obrazovkou.
 *
 * Nahrádza VBA formulár `UF_NahraditVsetko` z DAEX šablóny — s tým
 * rozdielom, že **appka si priradenie pamätá**. Druhý import tej istej
 * zákazky sa nepýta na nič.
 *
 * Nenamapovaná položka **blokuje import**: zákazka s dielcami bez
 * materiálu má nepravdivú cenu a nikto by si nevšimol, že tam chýba.
 */

type Stav = StavPolozky;

/** Filter cenníka podľa druhu položky — inak by zoznam začínal celým cenníkom. */
/** Kategórie, v ktorých žijú pracovné dosky (rovnaké ako na serveri). */
const KATEGORIE_PD = "PRACOVNA_DOSKA,ZASTENA";

export function filterPreDruh(
  p: MapovaniePolozka,
  akoPd: boolean,
): {
  druh: DruhMaterialu;
  sekcia: Sekcia;
  zMm?: string;
  kategoriaIncludeCsv?: string;
} {
  if (p.druh === "material") {
    // Pracovná doska je tyč 4100 mm na bežné metre — nie tabuľa
    if (akoPd) {
      return {
        druh: "dlzkovy",
        sekcia: "materialy",
        kategoriaIncludeCsv: KATEGORIE_PD,
        // Hrúbku dostala až v KOLE 120. `kontext` ju nesie
        // (`kluc_materialu`) a `predvyplneniePolozky()` ju pre PD ako
        // `z_mm` už používala — výber bol jediné miesto, kde sa
        // zahadzovala, takže na 38 mm dosku ponúkal aj 18 mm.
        zMm: p.kontext || undefined,
      };
    }
    return { druh: "plosny", sekcia: "materialy", zMm: p.kontext || undefined };
  }
  if (p.druh === "hrana") return { druh: "dlzkovy", sekcia: "hrany" };
  // Kovanie od Sklenárstva Mier sa vyberá zo skla, ktoré inak pickery
  // skrývajú (KOLO 164) — sekciu povie server (`sekcia_dodavatela`)
  return { druh: "kusovy", sekcia: p.sekcia === "sklo" ? "sklo" : "kovanie" };
}

/** Dielec je jednoznačný dvojicou (zostava, kód) — kód je v zostave unikátny. */
function klucDielca(d: DielecNahlad): string {
  return `${d.zostava_kod}|${d.kod}`;
}

function rozlozKlucDielca(k: string): [string, string] {
  const i = k.indexOf("|");
  return [k.slice(0, i), k.slice(i + 1)];
}

/** „18.00" → „18" — hrúbky chodia ako Decimal string. */
function mm(v: string | null): string {
  if (v == null) return "—";
  const n = Number(v);
  return Number.isFinite(n) ? String(n) : v;
}

/**
 * Čím predvyplniť formulár, keď sa položka zakladá do cenníka.
 *
 * Všetko, čo analýza o položke vie — nový dekor sa tak nemusí prepisovať
 * ručne. Názov z TopSolidu je zároveň dobrý odhad dekoru (`K682`).
 */
function predvyplneniePolozky(p: MapovaniePolozka): Predvyplnenie {
  // `K682  [18 mm]` → `K682`; hrúbka je už v `kontext`
  const nazov = p.popis.split("[")[0]?.trim() || p.popis;
  if (p.druh === "kovanie") {
    return { nazov, druh: "kusovy", mj: "ks" };
  }
  if (p.druh === "hrana") {
    return {
      nazov: `ABS ${p.material_popis || nazov} ${p.sirka_hrany_mm ?? ""}×${
        p.hrubka_hrany_mm ? Number(p.hrubka_hrany_mm) : ""
      }`.trim(),
      druh: "dlzkovy",
      mj: "bm",
      dekor_kod: p.material_popis || undefined,
      sirka_mm: p.sirka_hrany_mm != null ? String(p.sirka_hrany_mm) : undefined,
      hrubka_mm: p.hrubka_hrany_mm ?? undefined,
    };
  }
  // Hrúbka je v `kontext` — bez nej by sa nezložil kód ani neodvodila
  // kategória a import by tú istú položku nabudúce zase nenašiel
  if (p.je_pracovna_doska) {
    // Kupuje sa hotová na bežné metre — tyč, nie tabuľa (KOLO 85)
    return {
      nazov,
      druh: "dlzkovy",
      mj: "m",
      dekor_kod: nazov,
      kategoria: "PRACOVNA_DOSKA",
      z_mm: p.kontext || undefined,
    };
  }
  return {
    nazov,
    druh: "plosny",
    mj: "m2",
    dekor_kod: nazov,
    z_mm: p.kontext || undefined,
  };
}

/**
 * Poradie krokov je **príčinné**, nie kozmetické: delenie určuje, na aké
 * hrúbky sa pýta materiál (`hrubky_dielca`), a priradená doska určuje
 * dekor hrany (`HranovacImportu`). Do KOLA 89 bolo všetko na jednej
 * stránke a hrany boli vykreslené **nad** materiálmi — teda presne
 * naopak, než ako sa s tým pracuje.
 */
const KROKY: { kluc: Krok; nazov: string }[] = [
  { kluc: "dielce", nazov: "Dielce" },
  { kluc: "materialy", nazov: "Materiály a hrany" },
  { kluc: "kovania", nazov: "Kovania" },
];

function PasKrokov({
  krok,
  chybaMaterialov,
  chybaKovani,
  nalezy,
  onKrok,
}: {
  krok: Krok;
  chybaMaterialov: number;
  chybaKovani: number;
  /** Počet nálezov na krok — aby sa nemuselo klikať naslepo. */
  nalezy: Record<Krok, number>;
  onKrok: (k: Krok) => void;
}): JSX.Element {
  const index = KROKY.findIndex((x) => x.kluc === krok);
  return (
    <div className="flex flex-wrap items-center gap-1 border-b pb-2">
      {KROKY.map((x, i) => {
        const chyba =
          x.kluc === "materialy"
            ? chybaMaterialov
            : x.kluc === "kovania"
              ? chybaKovani
              : 0;
        const pocetNalezov = nalezy[x.kluc] ?? 0;
        // Späť sa dá kedykoľvek — user si potrebuje overiť, čo videl
        // v predošlom kroku. Dopredu len cez „Ďalej", aby sa hrany
        // neotvorili skôr, než majú z čoho vzniknúť.
        const dostupny = i <= index;
        return (
          <button
            key={x.kluc}
            type="button"
            disabled={!dostupny}
            onClick={() => onKrok(x.kluc)}
            className={cn(
              "flex items-center gap-1.5 rounded px-2 py-1 text-xs",
              i === index
                ? "bg-primary text-primary-foreground"
                : dostupny
                  ? "text-muted-foreground hover:bg-accent"
                  : "text-muted-foreground/50",
            )}
          >
            <span className="font-mono">{i + 1}</span>
            {x.nazov}
            {chyba > 0 ? (
              <span className="rounded bg-destructive/10 px-1 text-[10px] text-destructive">
                chýba {chyba}
              </span>
            ) : null}
            {pocetNalezov > 0 ? (
              <span
                className="rounded bg-warning/15 px-1 text-[10px] text-warning"
                title="Nálezy na opravu v TopSolide"
              >
                {pocetNalezov}×
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Bubliny hlavičiek rozmerových stĺpcov.
 *
 * Sú **konštanty**, lebo tie isté tri stĺpce sú v oboch tabuľkách kroku
 * Dielce (Delenie na vrstvy aj CNC opracovanie) — dva opísané texty by
 * sa rozišli.
 *
 * Názvy hovoria, **čo v stĺpci je** (KOLO 116). User: *„Podľa modelu je
 * vlastne rozmer — premenuj to. Proste chcem, aby to bolo jednoznačne
 * pomenované."* Do KOLA 116 sa ten istý údaj `prirez_x_mm` volal
 * v jednej tabuľke „Prírez (x/y)" a v druhej „Nový X × Y".
 */
const TITUL_CISTY =
  "CISTA_DELKA/CISTA_SIRKA z exportu — rozmer hotového dielca už aj " +
  "s hranami, nie holá doska";
const TITUL_PRIREZ =
  "Rozmer, na ktorý sa dielec odreže — po odčítaní hrán, nadmiere " +
  "operácie, zdvihu na minimum stroja, rotácii a združení";
const TITUL_TOPSOLID =
  "HRUBA_DELKA/HRUBA_SIRKA z exportu — prírez podľa TopSolidu. Appka " +
  "podľa neho nič neráta, je to na porovnanie; rozdiel o hrúbku hrán " +
  "je v poriadku (model počíta z olepeného rozmeru, dielňa reže holú " +
  "dosku).";

/** Skupina dielcov, ktoré import delí rovnakou hrúbkou. */
type SkupinaDelenia = {
  hrubka: string;
  vrstvy: string[];
  dielce: DielecNahlad[];
};

function skupinyDelenia(dielce: DielecNahlad[]): SkupinaDelenia[] {
  const mapa = new Map<string, SkupinaDelenia>();
  for (const d of dielce) {
    if (!d.delenie || d.z_mm == null) continue;
    const hrubka = mm(d.z_mm);
    const s = mapa.get(hrubka) ?? {
      hrubka,
      vrstvy: d.delenie.map(mm),
      dielce: [],
    };
    s.dielce.push(d);
    mapa.set(hrubka, s);
  }
  return [...mapa.values()].sort((a, b) => Number(a.hrubka) - Number(b.hrubka));
}

function Hlavicka({
  nadpis,
  pocet,
  zoSpolu,
  prepocitava,
  akcia,
}: {
  nadpis: string;
  pocet: number;
  zoSpolu?: number;
  prepocitava?: boolean;
  akcia?: JSX.Element;
}): JSX.Element {
  return (
    <h3 className="flex flex-wrap items-center gap-2 border-b bg-background-alt px-3 py-1.5 text-sm font-medium">
      {nadpis}
      <span className="text-xs font-normal text-muted-foreground">
        ({pocet}
        {zoSpolu !== undefined ? ` z ${zoSpolu}` : ""})
      </span>
      {prepocitava ? (
        <span className="text-xs font-normal text-muted-foreground">
          prepočítavam…
        </span>
      ) : null}
      {akcia ? <span className="ml-auto">{akcia}</span> : null}
    </h3>
  );
}

/**
 * Operácia slovom — v tabuľke je stĺpec úzky a kód `zdruz_rez` nič
 * nepovie. Presné znenie z Nastavení ide do `title` a do stĺpca
 * Označenie, kde ho dielňa aj tak potrebuje celé.
 */
function popisOperacie(p: PrirezNahlad): string {
  if (p.operacia === "zdruz_rez") {
    return `Združenie ${p.kusov_v_prireze}×`;
  }
  if (p.operacia === "rot_zdruz_rez") {
    return `Rotácia + ${p.kusov_v_prireze}×`;
  }
  if (p.operacia === "rot_rez") return "Rotácia";
  return "Predrezanie";
}

/**
 * Čo sa delí na vrstvy.
 *
 * Vlastná tabuľka, nie riadok medzi CNC opracovaním: delenie mení, na aké
 * hrúbky sa pýta materiál, kým rotácia a združovanie sú vec stroja. Sú to
 * dve rôzne rozhodnutia a v jednom zozname sa zle čítajú.
 *
 * Prepínač rotácie ani „Je to kovanie" tu **nie sú** — s lepením nemajú
 * nič spoločné.
 */
function TabulkaDelenia({
  dielce,
  delenie,
  onDelenie,
  prepocitava,
}: {
  dielce: DielecNahlad[];
  delenie: Delenie;
  onDelenie: (hrubka: string, vrstvy: number[] | null | undefined) => void;
  prepocitava: boolean;
}): JSX.Element | null {
  const skupiny = skupinyDelenia(dielce);
  const nedelene = Object.entries(delenie)
    .filter(([, v]) => v === null)
    .map(([k]) => k);
  const delene = dielce.filter((d) => d.delenie);
  // Lepený dielec, pre ktorý pravidlo delenia neexistuje (25 mm v
  // skripte). Rozhoduje operácia z exportu — hrúbka sama o tom nič
  // nehovorí, bežná 18 mm doska sa tiež nelepí.
  const bezRozbitia = dielce.filter(
    (d) => !d.delenie && d.operacia?.startsWith("lep"),
  );

  if (!skupiny.length && !nedelene.length && !bezRozbitia.length) return null;

  return (
    <section className="overflow-hidden rounded-lg border">
      <Hlavicka
        nadpis="Delenie na vrstvy"
        pocet={delene.length}
        prepocitava={prepocitava}
      />
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[170px]">Dielec</TableHead>
            <TableHead>Zostava</TableHead>
            <TableHead className="w-[70px] text-right">Hrúbka</TableHead>
            <TableHead className="w-[100px]">Skladba</TableHead>
            <TableHead className="w-[116px] text-right" title={TITUL_CISTY}>
              Rozmer po olepení
            </TableHead>
            <TableHead className="w-[84px] text-right">Hrany</TableHead>
            <TableHead className="w-[112px] text-right" title={TITUL_PRIREZ}>
              Rozmer prírezu
            </TableHead>
            <TableHead className="w-[128px] text-right" title={TITUL_TOPSOLID}>
              Rozmer s nadmierou (TopSolid)
            </TableHead>
            <TableHead className="w-12 text-right">Ks</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {nedelene.map((h) => (
            <TableRow key={`nedelene-${h}`} className="hover:bg-transparent">
              <TableCell
                colSpan={9}
                className="bg-background-alt/60 px-3 py-1.5 text-xs"
              >
                <span className="font-medium">{h} mm — nedelí sa</span>
                <span className="ml-2 text-muted-foreground">
                  materiál sa pýta v plnej hrúbke
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="float-right h-6 text-xs"
                  onClick={() => onDelenie(h, undefined)}
                >
                  Deliť podľa pravidla
                </Button>
              </TableCell>
            </TableRow>
          ))}

          {skupiny.map((sk) => (
            <Fragment key={sk.hrubka}>
              <TableRow className="hover:bg-transparent">
                <TableCell
                  colSpan={9}
                  className="bg-background-alt/60 px-3 py-1.5 text-xs"
                >
                  <Scissors
                    className="mr-1 inline h-3.5 w-3.5 text-primary"
                    aria-hidden
                  />
                  <span className="font-medium">
                    {sk.hrubka} mm → {sk.vrstvy.join(" + ")}
                  </span>
                  <span className="ml-2 text-muted-foreground">
                    {sk.dielce.length} dielcov
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="float-right h-6 text-xs"
                    onClick={() => onDelenie(sk.hrubka, null)}
                  >
                    Nedeliť
                  </Button>
                </TableCell>
              </TableRow>
              {sk.dielce.map((d) => (
                <RiadokDelenia key={klucDielca(d)} dielec={d} />
              ))}
            </Fragment>
          ))}

          {/* Lepené dielce, ktoré sa nerozbíjajú — v DAEX skripte je to
              sekcia „Nepotrebuju rozbitie". Doteraz ich tabuľka
              nespomenula vôbec, hoci sú to práve tie, pri ktorých sa
              človek pýta „a čo s týmto". */}
          {bezRozbitia.length > 0 ? (
            <>
              <TableRow className="hover:bg-transparent">
                <TableCell
                  colSpan={9}
                  className="bg-background-alt/60 px-3 py-1.5 text-xs"
                >
                  <span className="font-medium">Nepotrebujú rozbitie</span>
                  <span className="ml-2 text-muted-foreground">
                    lepené dielce, pre ktoré pravidlo delenia nie je —
                    materiál sa pýta v plnej hrúbke
                  </span>
                </TableCell>
              </TableRow>
              {bezRozbitia.map((d) => (
                <RiadokDelenia key={klucDielca(d)} dielec={d} />
              ))}
            </>
          ) : null}
        </TableBody>
      </Table>
    </section>
  );
}

/**
 * Čo o príreze hovorí TopSolid (`HRUBA_DELKA`/`HRUBA_SIRKA`).
 *
 * Appka podľa toho nič neráta — prírez si počíta pravidlom z operácie
 * a hrán. Stĺpec je tu preto, že rozdiel medzi modelom a pravidlom sa
 * inak nedá vidieť; do KOLA 105 sa `HRUBA_*` zahadzovalo už v parseri
 * a užívateľ ho musel hľadať očami v Exceli.
 *
 * Porovnáva sa proti `prirez_bez_upnutia_*`, teda prírezu **pred zdvihom
 * na minimum stroja** (KOLO 113). TopSolid rieši nadmiery, predĺženia na
 * stroj nie — do KOLA 113 sa porovnával `prirez_*` už so zdvihom, takže
 * oranžová svietila aj na `plat_lista` 601×37 (`lep_nc`, +5/+5), kde model
 * 606×42 aj pravidlo dali to isté. Šum, ktorý sa človek naučí
 * preklikávať, prikryje aj nález pravý.
 */
/**
 * Čo o príreze hovorí TopSolid (`HRUBA_DELKA`/`HRUBA_SIRKA`).
 *
 * Do KOLA 116 sa stĺpec volal „Podľa modelu" a nebolo z toho poznať,
 * o čí model ide — user: *„premenuj to, lebo neviem, o čo sa jedná."*
 *
 * **Farbu rozhoduje backend** (`sedi_s_modelom`), nie porovnanie tu.
 * Pri postupe s vlastným predfrezom sa od modelu vedome líšime o hrúbku
 * hrán a to vie len backend; keď si porovnanie robil frontend sám, po
 * zmene pravidla v KOLE 115 ostal na starom — nález zhasol, ale
 * oranžová svietila ďalej na každom lepenom dielci s hranou a jej
 * bublina odkazovala na nález, ktorý už nevznikal.
 */
function PodlaModelu({ dielec }: { dielec: DielecNahlad }): JSX.Element {
  if (dielec.hruba_x_mm == null || dielec.hruba_y_mm == null) {
    return (
      <TableCell className="px-3 py-1.5 text-right text-xs text-muted-foreground">
        —
      </TableCell>
    );
  }
  const sedi = dielec.sedi_s_modelom !== false;
  return (
    <TableCell
      className={`whitespace-nowrap px-3 py-1.5 text-right font-mono text-xs ${
        sedi
          ? "text-muted-foreground"
          : "rounded bg-warning/15 font-medium text-warning"
      }`}
      title={
        sedi
          ? "Rozmer z TopSolidu sedí s pravidlom. Rozdiel o hrúbku hrán je v poriadku — model počíta nadmieru z olepeného rozmeru, dielňa reže holú dosku."
          : "TopSolid hovorí inú nadmieru, než má — nález je v zozname „Na opravu v TopSolide\""
      }
    >
      {mm(dielec.hruba_x_mm)} × {mm(dielec.hruba_y_mm)}
    </TableCell>
  );
}

/** Riadok tabuľky delenia — čistý rozmer vedľa prírezu, ako v skripte. */
/**
 * Koľko z rozmeru ubrali hrany (KOLO 115).
 *
 * `CISTA_*` z TopSolidu je rozmer **po olepení**, takže prírez vzniká
 * z holej dosky: `čistý − hrany + nadmiera`. Bez tohto stĺpca sa
 * z tabuľky nedalo prečítať, prečo je prírez o 2 mm menší, než hovorí
 * model — a práve to bolo na `lep_nc` sporné.
 *
 * Číslo počíta backend (`odpocet_hran_xy`); odčítať si ho tu zo stĺpcov
 * Čistý a Prírez by nešlo, lebo v príreze už sedí aj nadmiera operácie
 * a zdvih na minimum stroja.
 */
function OdpocetHran({ dielec }: { dielec: DielecNahlad }): JSX.Element {
  const x = Number(dielec.odpocet_x_mm ?? 0);
  const y = Number(dielec.odpocet_y_mm ?? 0);
  if (!x && !y) {
    return (
      <TableCell className="px-3 py-1.5 text-right text-xs text-muted-foreground">
        —
      </TableCell>
    );
  }
  return (
    <TableCell
      className="whitespace-nowrap px-3 py-1.5 text-right font-mono text-xs text-muted-foreground"
      title="Hrany sú v čistom rozmere z TopSolidu započítané — z prírezu sa preto odčítajú, reže sa holá doska"
    >
      −{mm(dielec.odpocet_x_mm)} / −{mm(dielec.odpocet_y_mm)}
    </TableCell>
  );
}

function RiadokDelenia({ dielec }: { dielec: DielecNahlad }): JSX.Element {
  return (
    <TableRow>
      <TableCell className="px-3 py-1.5 font-mono text-xs">
        {dielec.kod}
      </TableCell>
      <TableCell
        className="max-w-0 truncate px-3 py-1.5 text-xs text-muted-foreground"
        title={dielec.zostava_kod}
      >
        {dielec.zostava_kod}
      </TableCell>
      <TableCell className="whitespace-nowrap px-3 py-1.5 text-right font-mono text-xs">
        {mm(dielec.z_mm)}
      </TableCell>
      <TableCell className="whitespace-nowrap px-3 py-1.5 text-xs">
        {dielec.delenie
          ? `${dielec.delenie.map(mm).join(" + ")} mm`
          : `${mm(dielec.z_mm)} mm`}
      </TableCell>
      <TableCell className="whitespace-nowrap px-3 py-1.5 text-right font-mono text-xs">
        {mm(dielec.x_mm)} × {mm(dielec.y_mm)}
      </TableCell>
      <OdpocetHran dielec={dielec} />
      <TableCell className="whitespace-nowrap px-3 py-1.5 text-right font-mono text-xs">
        {/* Prírez ráta ten istý `cnc_prirezy` ako cena a porez a backend
            ho posiela **vždy**. Do KOLA 105 sa čítal z `prirez`, ktorý
            vzniká len pri operácii stroja, takže `plat` 1602×502 tu mal
            napísané 1602×502 — teda rozmer dielca, nie prírez. */}
        {mm(dielec.prirez_x_mm)} × {mm(dielec.prirez_y_mm)}
      </TableCell>
      <PodlaModelu dielec={dielec} />
      <TableCell className="px-3 py-1.5 text-right text-xs">
        {dielec.pocet_ks}
      </TableCell>
    </TableRow>
  );
}

/** Rozdiel dvoch rozmerov bez chvosta z desatinných čísel (902 − 2). */
function rozdiel(a: string | null | undefined, b: string | null | undefined): string {
  return mm(String(Math.round((Number(a ?? 0) - Number(b ?? 0)) * 100) / 100));
}

/** Odpočet jednej osi — „—“, keď hrana v tej osi nie je (nie „−0“). */
function odpocetOsi(v: string | null | undefined): string {
  return Number(v ?? 0) === 0 ? "—" : `−${mm(v ?? null)}`;
}

/** Hrany mimo strán zmenili odpočet — holá doska je z neolepeného dielca. */
function podlaNeolepeneho(d: DielecNahlad): boolean {
  return (
    Number(d.hrany_navyse_x_mm ?? 0) !== 0 ||
    Number(d.hrany_navyse_y_mm ?? 0) !== 0
  );
}

/**
 * Odčítanie hrán pri postupe najprv na CNC (KOLO 173).
 *
 * User: *„chcem, aby si pri importe z TopSolidu to odčítanie hrán tam
 * dal — aby som videl, že oproti môjmu exportu niečo meníš, ja to
 * prejdem"*. `nc_olep`, `lep_nc_olep` a `lep_nc_olep_nc` režú holú dosku:
 * rozmer po olepení z TopSolidu − hrany = holá doska, + nadmiera = prírez.
 *
 * Navrchu sú dielce, kde export nesie neolepený dielec a jeho rozmer
 * nesedí s hranami na stranách — zaoblený roh, ktorý `HRANA_1–4` neoznačia
 * (`bok_P` 902×502 s tromi hranami by inak vyšiel holý 901×500).
 * Odpočet aj holú dosku počíta backend; tu sa len skladajú do riadku.
 */
function TabulkaOdcitaniaHran({
  dielce,
}: {
  dielce: DielecNahlad[];
}): JSX.Element | null {
  const riadky = dielce.filter(
    (d) =>
      d.najprv_cnc &&
      (Number(d.odpocet_x_mm ?? 0) !== 0 || Number(d.odpocet_y_mm ?? 0) !== 0),
  );
  if (!riadky.length) return null;
  const nesedia = riadky.filter(podlaNeolepeneho);
  const zoradene = [...nesedia, ...riadky.filter((d) => !podlaNeolepeneho(d))];

  return (
    <section className="overflow-hidden rounded-lg border">
      <Hlavicka
        nadpis="Odčítanie hrán — najprv na CNC"
        pocet={riadky.length}
        akcia={
          nesedia.length ? (
            <span className="text-xs font-normal text-warning">
              {nesedia.length} podľa neolepeného dielca
            </span>
          ) : undefined
        }
      />
      <p className="border-b px-3 py-1.5 text-xs text-muted-foreground">
        Rozmer po olepení z TopSolidu − hrany = holá doska, + nadmiera postupu
        = prírez. Kde export nesie neolepený dielec, holá doska je z neho.
      </p>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[170px]">Dielec</TableHead>
            <TableHead>Zostava</TableHead>
            <TableHead className="w-[104px]">Postup</TableHead>
            {/* Tie isté názvy ako Delenie a CNC (KOLO 116) */}
            <TableHead className="w-[116px] text-right" title={TITUL_CISTY}>
              Rozmer po olepení
            </TableHead>
            <TableHead className="w-[96px] text-right">Hrany</TableHead>
            <TableHead
              className="w-[110px] text-right"
              title="Rozmer po olepení − hrany: doska, ktorú reže CNC"
            >
              Holá doska
            </TableHead>
            <TableHead className="w-[112px] text-right" title={TITUL_PRIREZ}>
              Rozmer prírezu
            </TableHead>
            <TableHead className="w-[128px] text-right" title={TITUL_TOPSOLID}>
              Rozmer s nadmierou (TopSolid)
            </TableHead>
            <TableHead className="w-12 text-right">Ks</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {zoradene.map((d) => (
            <RiadokOdcitania key={klucDielca(d)} dielec={d} />
          ))}
        </TableBody>
      </Table>
    </section>
  );
}

function RiadokOdcitania({ dielec: d }: { dielec: DielecNahlad }): JSX.Element {
  const nesedi = podlaNeolepeneho(d);
  const bunka = "whitespace-nowrap px-3 py-1.5 text-right font-mono text-xs";
  return (
    <>
      <TableRow className={cn(nesedi && "bg-warning/10 hover:bg-warning/15")}>
        <TableCell className="px-3 py-1.5 font-mono text-xs">
          {d.kod}
          {nesedi ? (
            <span
              className="ml-1 font-sans font-semibold text-warning"
              title="Odčítané podľa neolepeného dielca — hrany v exporte nesedia"
              aria-label="Odčítané podľa neolepeného dielca"
            >
              !
            </span>
          ) : null}
        </TableCell>
        <TableCell
          className="max-w-0 truncate px-3 py-1.5 text-xs text-muted-foreground"
          title={d.zostava_kod}
        >
          {d.zostava_kod}
        </TableCell>
        <TableCell className="px-3 py-1.5 font-mono text-xs">
          {d.operacia ?? "—"}
        </TableCell>
        <TableCell className={bunka}>
          {mm(d.x_mm)} × {mm(d.y_mm)}
        </TableCell>
        <TableCell className={bunka}>
          {odpocetOsi(d.odpocet_x_mm)} / {odpocetOsi(d.odpocet_y_mm)}
          {d.neolepeny_x_mm == null ? (
            <span className="block font-sans text-[10px] text-muted-foreground">
              podľa hrán
            </span>
          ) : null}
        </TableCell>
        <TableCell className={cn(bunka, "font-medium")}>
          {rozdiel(d.x_mm, d.odpocet_x_mm)} × {rozdiel(d.y_mm, d.odpocet_y_mm)}
        </TableCell>
        <TableCell className={bunka}>
          {mm(d.prirez_x_mm)} × {mm(d.prirez_y_mm)}
        </TableCell>
        <PodlaModelu dielec={d} />
        <TableCell className="px-3 py-1.5 text-right text-xs">
          {d.pocet_ks}
        </TableCell>
      </TableRow>
      {nesedi ? (
        <TableRow className="bg-warning/10 hover:bg-warning/15">
          <TableCell colSpan={9} className="px-3 pb-1.5 pt-0 text-xs text-warning">
            hrany v exporte{" "}
            {odpocetOsi(rozdiel(d.odpocet_x_mm, d.hrany_navyse_x_mm))} /{" "}
            {odpocetOsi(rozdiel(d.odpocet_y_mm, d.hrany_navyse_y_mm))}, neolepený
            dielec {mm(d.neolepeny_x_mm ?? null)} × {mm(d.neolepeny_y_mm ?? null)} →
            odčítané podľa neolepeného dielca
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
}

/** Rotácia a združovanie — čo s dielcom spraví stroj. */
function TabulkaCnc({
  dielce,
  rotacie,
  onRotacia,
}: {
  dielce: DielecNahlad[];
  rotacie: Record<string, boolean>;
  onRotacia: (kluc: string, v: boolean | undefined) => void;
}): JSX.Element | null {
  const [vsetky, setVsetky] = useState(false);
  // Default len to, kde stroj niečo robí — inak by tu bolo 140 riadkov.
  // `prirez` je vyplnený pri **každej** operácii, teda aj pri predreze;
  // do KOLA 88 sa filtrovalo len na združenie a predrez tu nebol vôbec,
  // preto appka ukázala 4 riadky tam, kde DAEX skript 19.
  const zaujimave = dielce.filter((d) => d.rotacia || d.prirez);
  const zobrazene = vsetky ? dielce : zaujimave;

  if (!dielce.length) return null;

  return (
    <section className="overflow-hidden rounded-lg border">
      <Hlavicka
        nadpis="CNC opracovanie"
        pocet={zobrazene.length}
        zoSpolu={dielce.length}
        akcia={
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-6 text-xs"
            onClick={() => setVsetky((v) => !v)}
          >
            {vsetky ? "Len zmeny" : `Ukázať všetkých ${dielce.length}`}
          </Button>
        }
      />
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[170px]">Dielec</TableHead>
            <TableHead>Zostava</TableHead>
            <TableHead className="w-[110px] text-right" title={TITUL_CISTY}>
              Rozmer po olepení
            </TableHead>
            <TableHead className="w-[104px]">Operácia</TableHead>
            <TableHead className="w-[104px] text-right" title={TITUL_PRIREZ}>
              Rozmer prírezu
            </TableHead>
            <TableHead className="w-[122px] text-right" title={TITUL_TOPSOLID}>
              Rozmer s nadmierou (TopSolid)
            </TableHead>
            <TableHead className="w-[150px]">Rotácia</TableHead>
            <TableHead className="w-[170px]">Označenie</TableHead>
            <TableHead className="w-12 text-right">Ks</TableHead>
            <TableHead className="w-[70px] text-right">Prírezov</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {zobrazene.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={10}
                className="py-6 text-center text-xs text-muted-foreground"
              >
                Žiadny dielec nepotrebuje predrez, združenie ani rotáciu.
              </TableCell>
            </TableRow>
          ) : (
            zobrazene.map((d) => {
              const kluc = klucDielca(d);
              const volba = rotacie[kluc];
              return (
                <TableRow key={kluc}>
                  <TableCell className="px-3 py-1.5 font-mono text-xs">
                    {d.kod}
                  </TableCell>
                  <TableCell
                    className="max-w-0 truncate px-3 py-1.5 text-xs text-muted-foreground"
                    title={d.zostava_kod}
                  >
                    {d.zostava_kod}
                  </TableCell>
                  <TableCell className="whitespace-nowrap px-3 py-1.5 text-right font-mono text-xs">
                    {mm(d.x_mm)}×{mm(d.y_mm)}
                  </TableCell>
                  <TableCell className="px-3 py-1.5 text-xs">
                    {/* Združovanie ani predrez sa prepnúť nedajú — rátajú
                        sa z rozmerov a minima stroja, preto len štítok */}
                    {d.prirez ? (
                      <span
                        className="whitespace-nowrap rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-900 dark:text-amber-200"
                        title={d.prirez.operacia_nazov}
                      >
                        {popisOperacie(d.prirez)}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap px-3 py-1.5 text-right font-mono text-xs">
                    {mm(d.prirez_x_mm)}×{mm(d.prirez_y_mm)}
                  </TableCell>
                  <PodlaModelu dielec={d} />
                  <TableCell className="px-3 py-1.5">
                    <select
                      className="h-6 w-full rounded border bg-background px-1 text-xs disabled:cursor-not-allowed disabled:opacity-50"
                      aria-label={`Rotácia — ${d.kod}`}
                      disabled={!d.rotacia_mozna}
                      title={
                        d.rotacia_mozna
                          ? "Otočiť dielec o 90° kvôli kolíkovaniu na čelách"
                          : "Dielec sa nezmestí do rotačného priestoru stroja — otočiť sa nedá"
                      }
                      value={
                        volba === undefined ? "auto" : volba ? "ano" : "nie"
                      }
                      onChange={(e) =>
                        onRotacia(
                          kluc,
                          e.target.value === "auto"
                            ? undefined
                            : e.target.value === "ano",
                        )
                      }
                    >
                      <option value="auto">
                        auto{d.rotacia ? " (rotuje)" : ""}
                      </option>
                      <option value="ano">rotovať</option>
                      <option value="nie">nerotovať</option>
                    </select>
                  </TableCell>
                  <TableCell
                    className="max-w-0 truncate px-3 py-1.5 font-mono text-xs text-muted-foreground"
                    title={d.prirez?.oznacenie || undefined}
                  >
                    {d.prirez?.oznacenie || "—"}
                  </TableCell>
                  <TableCell className="px-3 py-1.5 text-right text-xs">
                    {d.pocet_ks}
                  </TableCell>
                  <TableCell className="px-3 py-1.5 text-right text-xs">
                    {d.prirez ? d.prirez.prirezov : d.pocet_ks}
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </section>
  );
}

/** Výber zostáv z Tandem Zákazky — Určení vybraných a koľko ich program poslal. */
interface VyberZostav {
  urceni: string[];
  celkom: number | null;
  /** Diely Tandemu z konfigurátora regálu CRC (KOLO 232) — iné texty sprievodcu. */
  regal?: boolean;
}

/**
 * Pás nad sprievodcom pri importe len vybraných zostáv (KOLO 175) — bez
 * neho by „2 zostavy" v náhľade vyzerali ako chyba exportu.
 */
export function PasVyberu({ vyber }: { vyber: VyberZostav }): JSX.Element {
  return (
    <p
      role="status"
      className="flex items-center gap-2 rounded-md border bg-muted/50 px-3 py-2 text-sm"
      title={vyber.urceni.join(", ")}
    >
      <ListChecks aria-hidden className="h-4 w-4 shrink-0 text-primary" />
      {vyber.regal ? (
        <span>
          Len diely regálu CRC — ostatné zostavy nábytku sa nemenia. Priraď materiály a hrany,
          na konci uvidíš, čo pribudne.
        </span>
      ) : (
        <span>
          Len vybrané zostavy z Tandem Zákazky:{" "}
          <strong>
            {vyber.urceni.length}
            {vyber.celkom ? ` z ${vyber.celkom}` : ""}
          </strong>{" "}
          — ostatné zostavy zákazky sa nemenia.
        </span>
      )}
    </p>
  );
}

/**
 * Nová časť existujúcej zákazky (KOLO 203) — export s iným dovetkom
 * Určení. Mapovanie je to isté ako pri novej zákazke, končí sa „Pridať
 * časť" (`POST /import/topsolid/cast/{zakazka_id}`).
 */
export interface CastCiel {
  zakazkaId: string;
  nazov: string;
  /** Názov doterajšieho výpočtu zákazky bez častí — stane sa prvou časťou. */
  doterajsiaNazov: string | null;
}

export function TopSolidMapovanieDialog({
  open,
  onOpenChange,
  onImported,
  update,
  cast = null,
  kVypoctuZOkna = null,
  konceptNaOtvorenie,
  onKonceptOtvoreny,
  onVyberKoncept,
  onAktivnyKoncept,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** `variantaId` len pri novej časti — otvorí sa jej záložka. */
  onImported?: (zakazkaId: string, variantaId?: string) => void;
  /**
   * Režim **update** (KOLO 118) — opravený export do existujúceho
   * výpočtu namiesto novej zákazky.
   *
   * Mapovanie je to isté (endpoint bez namapovaných položiek vráti 422),
   * takže sa nestavia druhý sprievodca; mení sa len to, čím sa končí:
   * namiesto „Importovať" je „Zobraziť zmeny" a náhľad rozdielov.
   */
  update?: { zakazkaId: string; variantaId: string } | null;
  /** Režim **nová časť** existujúcej zákazky (KOLO 203). */
  cast?: CastCiel | null;
  /**
   * Výroba patrí k výpočtu (KOLO 208) — vybrané v okne nového importu;
   * v sprievodcovi sa dá zmeniť pri Druhu čísla.
   */
  kVypoctuZOkna?: ZakazkaNaPripojenie | null;
  /**
   * Otvor **tento** rozrobený import (KOLO 127) — odkaz z programu Tandem
   * Zostavy `/zakazky?koncept=<id>`. Plávajúci pruh ponúka len najnovší;
   * program ich však môže poslať viac za sebou a odkaz musí otvoriť ten,
   * na ktorý sa kliklo. `onKonceptOtvoreny` ho potom vynuluje, aby sa pri
   * ďalšom otvorení okna neobnovoval znova.
   */
  konceptNaOtvorenie?: string | null;
  onKonceptOtvoreny?: () => void;
  /**
   * Klik na plávajúci pruh (KOLO 128). Rodič vie, v akom režime koncept
   * otvoriť — aktualizácia potrebuje výpočet a keď ho koncept nemá,
   * najprv sa spýta. Bez rodiča sa koncept obnoví ako doteraz.
   */
  onVyberKoncept?: (k: Koncept) => void;
  /** Na ktorom koncepte sprievodca stojí — rodič podľa toho neponúka ten
   *  istý koncept v okne nového importu (KOLO 216). */
  onAktivnyKoncept?: (konceptId: string | null) => void;
}): JSX.Element {
  const analyza = useTopsolidAnalyza();
  const potvrdit = useTopsolidPotvrdit();
  const pridatCast = useTopsolidPridajCast(cast?.zakazkaId ?? "");

  const [file, setFile] = useState<File | null>(null);
  const [cislo, setCislo] = useState("");
  // Druh čísla (KOLO 200): zákazka AA, výpočet V (AA neskôr) alebo malá
  // zákazka bez AA (Z). Z z Tandem Zákazky je už v `cislo` a nemení sa.
  const [druh, setDruh] = useState<DruhCisla>("zakazka");
  const [vykresCesta, setVykresCesta] = useState<string | null>(null);
  // Výpočet, ku ktorému import pripojí výrobu (KOLO 208)
  const [kVypoctu, setKVypoctu] = useState<ZakazkaNaPripojenie | null>(null);
  const [vyberVypoctu, setVyberVypoctu] = useState(false);
  useEffect(() => {
    if (kVypoctuZOkna) setKVypoctu(kVypoctuZOkna);
  }, [kVypoctuZOkna]);
  const [nazov, setNazov] = useState("");
  // Z pridelené Rozpočtom (Tandem Zákazka ho už zapísala do výkresu)
  const zZRozpoctu = druh === "mala" && /^Z\d+$/.test(cislo.trim());
  const cisloOk = druh !== "zakazka" || cislo.trim() !== "";
  const [data, setData] = useState<TopsolidAnalyza | null>(null);
  const [stavy, setStavy] = useState<Record<string, Stav>>({});
  // Názvy ručne vybraných hrán — v bunke tabuľky sa musí ukázať, čo si
  // vybral, a analýza ich nevracia (server pozná id, nie našu voľbu).
  const [nazvyHran, setNazvyHran] = useState<Record<string, string>>({});
  const [krok, setKrok] = useState<Krok>("dielce");
  // Voľby z náhľadu dielcov. Delenie mení otázky na materiál, takže
  // sa prepočítava na serveri; rotácia nie, tá ide až pri importe.
  const [delenie, setDelenie] = useState<Delenie>({});
  const [rotacie, setRotacie] = useState<Record<string, boolean>>({});
  // Zaradenie mení otázky na materiál rovnako ako delenie, takže sa
  // prepočítava na serveri
  const [zaradenie, setZaradenie] = useState<Zaradenie>({});
  // „Je to pracovná / plošná doska" (KOLO 168) — mení, z ktorého cenníka
  // sa doska ponúka, takže sa tiež prepočítava na serveri
  const [pracovneDosky, setPracovneDosky] = useState<PracovneDosky>({});
  /** Čo už na server odišlo — bráni prepočtu hneď po prvej analýze. */
  const poslednePoslane = useRef<string | null>(null);

  // ── Rozrobený import (KOLO 117) ──────────────────────────
  // Do tohto kola žil sprievodca len v pamäti prehliadača a klik vedľa
  // dialógu zmazal aj hodinu priraďovania. Koncept na serveri drží
  // nahratý súbor aj rozhodnutia, takže sa dá zavrieť okno, vypnúť
  // appka aj presadnúť k inému počítaču.
  const koncepty = useKoncepty();
  const zalozKoncept = useZalozKoncept();
  const ulozKoncept = useUlozKoncept();
  const zmazKoncept = useZmazKoncept();
  /** Koncept, do ktorého sa autosave-uje; `null` = ešte nie je súbor. */
  const [konceptId, setKonceptId] = useState<string | null>(null);
  useEffect(() => {
    onAktivnyKoncept?.(konceptId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [konceptId]);
  /** Kedy prišiel súbor, ktorý je načítaný (KOLO 216). Opravený export
   *  z TopSolidu vymení súbor v tom istom koncepte a zoznam konceptov
   *  (obnovuje sa každých 15 s) prinesie iný čas — súbor sa načíta znova. */
  const suborAt = useRef<string | null>(null);
  const oznacVideny = useOznacVideny();
  /** Mená zostáv z Tandem Zostáv `{urceni: jmeno}` (KOLO 146) — prídu
   *  s konceptom a idú späť pri potvrdení aj aktualizácii. Pri nahratí
   *  opraveného exportu ostávajú (tá istá zákazka, párujú sa podľa urceni). */
  const [zostavyMena, setZostavyMena] = useState<Record<string, string> | null>(null);
  /** Výber zostáv z Tandem Zákazky (KOLO 175) — prichádza s konceptom
   *  a ide do analýzy, potvrdenia aj aktualizácie; pri nahratí opraveného
   *  exportu ostáva. Ref, lebo `nacitaj` beží hneď po obnove konceptu,
   *  skôr než sa nový stav prekreslí. */
  const [vyberZostav, setVyberZostav] = useState<VyberZostav | null>(null);
  const vyberRef = useRef<string[] | null>(null);
  function nastavVyber(v: VyberZostav | null): void {
    vyberRef.current = v?.urceni ?? null;
    setVyberZostav(v);
  }
  /** „Ukončiť import?" — zatvorenie nesmie byť jeden omylný klik. */
  const [pytaSa, setPytaSa] = useState(false);
  /** Čo prežilo posledné znovu-nahratie — veta pod výberom súboru. */
  const [prevzate, setPrevzate] = useState<{
    ostalo: number;
    nove: number;
  } | null>(null);

  // ── Režim update (KOLO 118) ──────────────────────────────
  const [zmenyOtvorene, setZmenyOtvorene] = useState(false);
  const [nahlad, setNahlad] = useState<UpdateNahlad | null>(null);
  const nahladMut = useUpdateNahlad(update?.variantaId ?? "");
  const vykonatMut = useUpdateVykonat(
    update?.zakazkaId ?? "",
    update?.variantaId ?? "",
  );

  const vsetky = useMemo(
    () => (data ? [...data.materialy, ...data.hrany, ...data.kovania] : []),
    [data],
  );
  // Hranu, ktorú appka vybrala podľa dekoru, netreba priraďovať —
  // rovnaké pravidlo má `chybajuce` na serveri, inak by sa tlačidlo
  // a odpoveď servera nezhodli (`jeVyriesena`).
  const nevyriesene = vsetky.filter(
    (p) => !jeVyriesena(p, stavy[klucPolozky(p)]),
  );
  // Materiál a hrana sú od KOLA 114 **jeden krok**: dekor hrany sa
  // odvodzuje z priradenej dosky, takže priradíš dosku a hrany k nej
  // pribudnú hneď pod ňou. Kovanie od nich nezávisí (je to čistý lookup
  // kódu v cenníku), preto ide až nakoniec a nič neblokuje — dovtedy
  // sedelo v kroku 2 a **muselo sa domapovať skôr, než sa dalo pozrieť
  // na hrany**, čo je presne naopak, než ako to závisí.
  const chybaKroku2 = nevyriesene.filter((p) => p.druh !== "kovanie");
  const chybaKovani = nevyriesene.filter((p) => p.druh === "kovanie");

  /** Koľko nálezov patrí ktorému kroku — číslo na páse krokov. */
  const nalezyPreKrok = useMemo((): Record<Krok, number> => {
    const o = data?.opravy ?? [];
    return {
      dielce: opravyPreKrok(o, "dielce").length,
      materialy: opravyPreKrok(o, "materialy").length,
      // Posledný krok nesie úplný zoznam, nie len svoje — je to súhrn
      // pred potvrdením.
      kovania: o.length,
    };
  }, [data]);

  /** Jedna cesta pre zmenu položky — tabuľky aj výber idú cezňu. */
  function zmenStav(k: string, novy: Stav, nazov?: string): void {
    setStavy((prev) => ({ ...prev, [k]: novy }));
    if (nazov) setNazvyHran((prev) => ({ ...prev, [k]: nazov }));
  }

  function reset(): void {
    setFile(null);
    setData(null);
    setKrok("dielce");
    setStavy({});
    setNazvyHran({});
    setDelenie({});
    setRotacie({});
    setZaradenie({});
    setPracovneDosky({});
    poslednePoslane.current = null;
    setCislo("");
    setDruh("zakazka");
    setVykresCesta(null);
    setKVypoctu(null);
    setVyberVypoctu(false);
    setNazov("");
    setKonceptId(null);
    suborAt.current = null;
    setZostavyMena(null);
    nastavVyber(null);
    setPytaSa(false);
    setPrevzate(null);
  }

  /**
   * Ktorá doska je priradená dekoru — pre zoznam detegovaných hrán.
   *
   * `kontext` hrany je normalizovaný materiál (`kluc_hrany` na serveri)
   * a materiálová položka má ten istý reťazec v `token`, takže sa to
   * páruje bez normalizácie v TS. Dekor býva vo viacerých hrúbkach;
   * berie sa prvá vyriešená — dekor je pri všetkých ten istý, presne
   * ako to robí server pri hľadaní hrany.
   */
  /**
   * Doska z cenníka priradená dekoru — id aj popis.
   *
   * Popis sa berie z tej istej mapy názvov, akú používa tabuľka položiek
   * (`nazvyHran`), takže hlavička skupiny hrán ukáže presne to, čo user
   * vybral, a nie surový reťazec z TopSolidu (KOLO 124).
   */
  function doskaPreToken(token: string): PriradenaDoska | null {
    for (const p of data?.materialy ?? []) {
      if (p.token !== token) continue;
      const s = stavy[klucPolozky(p)];
      const id = s?.material_id ?? p.zakazka_material_id ?? p.naucene_material_id;
      if (!id) continue;
      // `||`, nie `??` — prázdny `auto_kod` materiálu by zastavil hľadanie
      // a hlavička by ukázala reťazec z TopSolidu aj pri zapamätanej doske
      const zPamate =
        id === p.zakazka_material_id
          ? p.zakazka_kod || p.zakazka_nazov
          : id === p.naucene_material_id
            ? p.naucene_kod || p.naucene_nazov
            : "";
      const popis =
        nazvyHran[klucPolozky(p)] || zPamate || p.auto_kod || p.auto_nazov || null;
      return { id, popis };
    }
    return null;
  }

  /** Priradenia dosiek pre server — z nich sa odvodia hrany. */
  function priradenia(stavyTeraz: Record<string, Stav>): Materialy {
    const out: Materialy = {};
    for (const [kluc, s] of Object.entries(stavyTeraz)) {
      if (!kluc.startsWith("material|") || !s.material_id) continue;
      // `material|token|kontext` → `token|kontext` (tvar zo servera)
      out[kluc.slice("material|".length)] = s.material_id;
    }
    return out;
  }

  async function nacitaj(
    f: File,
    volba: Delenie = {},
    zaradenieVolba: Zaradenie = {},
    materialyVolba: Materialy = {},
    pdVolba: PracovneDosky = {},
  ): Promise<TopsolidAnalyza | null> {
    setFile(f);
    try {
      const { data: a } = await analyza.mutateAsync({
        file: f,
        delenie: volba,
        zaradenie: zaradenieVolba,
        materialy: materialyVolba,
        pracovneDosky: pdVolba,
        zostavyVybrane: vyberRef.current,
        // Aktualizácia hotovej zákazky — priradenia zo zákazky (KOLO 217)
        variantaId: update?.variantaId ?? null,
      });
      setData(a);
      // Číslo a názov zákazky z označenia hlavnej zostavy (KOLO 125).
      // **Len do prázdneho poľa** — funkcionálny tvar je podstatný:
      // nesmie prepísať, čo user napísal, ani čo obnovil rozrobený
      // koncept, a `nacitaj` beží znova po každej zmene delenia.
      // Číslo býva prázdne (nesie ho len časť exportov), názov skoro vždy.
      if (a.zakazka_kod) setCislo((p) => p || a.zakazka_kod || "");
      // Z7_… z Určení = malá zákazka bez AA (KOLO 200)
      if (a.zakazka_kod && /^Z\d+$/.test(a.zakazka_kod)) {
        setDruh((d) => (d === "zakazka" ? "mala" : d));
      }
      if (a.zakazka_nazov) setNazov((p) => p || a.zakazka_nazov || "");
      // Naučené priradenia sú predvyplnené — to je celý zmysel pamäte.
      // Pri prepočte po zmene delenia sa už rozhodnuté položky nesmú
      // zahodiť — pribudnú/ubudnú len tie, ktorých sa hrúbka týka.
      setStavy((prev) => {
        const init: Record<string, Stav> = {};
        for (const p of [...a.materialy, ...a.hrany, ...a.kovania]) {
          const k = klucPolozky(p);
          // Pri aktualizácii vyhrá to, čo majú dielce v zákazke teraz,
          // nad pamäťou (KOLO 217) — server rozhoduje rovnako
          // (`rozhodnutie_mapa`)
          init[k] = prev[k] ?? {
            material_id: p.zakazka_material_id ?? p.naucene_material_id,
            zmazat: p.zakazka_material_id ? false : p.naucene_zmazat,
            // Default žije v `PRAZDNY_STAV` — tu len ten istý
            zapamatat: PRAZDNY.zapamatat,
          };
        }
        return init;
      });
      return a;
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Súbor sa nepodarilo prečítať",
        description: extractErrorMessage(e),
      });
      setFile(null);
      return null;
    }
  }

  /** Snapshot sprievodcu pre server — jeden tvar pre zápis aj obnovu. */
  function stavPreServer(krokTeraz: Krok = krok): KonceptStav {
    return {
      cislo_zakazky: cislo.trim(),
      nazov: nazov.trim(),
      krok: krokTeraz,
      // Koncept aktualizácie si pamätá výpočet (KOLO 128); bez toho sa
      // minimalizovaný update po obnovení tváril ako nový import
      ...(update ? { varianta_id: update.variantaId, cast_nova: false } : {}),
      // Nová časť (KOLO 203) — okno sa po obnove spýta znova s týmto názvom
      ...(cast ? { cast_nova: true, cast: cast.nazov } : {}),
      stav_json: JSON.stringify({
        stavy,
        nazvyHran,
        delenie,
        rotacie,
        zaradenie,
        pracovneDosky,
        druh,
        kVypoctu,
      }),
    };
  }

  /**
   * Nahratie súboru — prvé aj **znovu-nahratie opraveného exportu**.
   *
   * Rozhodnutia sa neposielajú znova nadarmo: `nacitaj` skladá `stavy`
   * cez `prev[k] ?? …`, takže priradenia prežijú. Do KOLA 117 sem
   * ale chodilo holé `nacitaj(f)` — bez `delenie`, `zaradenie`
   * a `materialy` —, takže sa analýza vrátila, akoby si nič nerozdelil
   * ani nepreklasifikoval. Odtiaľ tá povesť, že po oprave v TopSolide
   * treba klikať všetko odznova.
   */
  async function nahraj(f: File): Promise<void> {
    if (await prevezmiSubor(f)) await odlozKoncept(f);
  }

  /** Nový súbor s doterajším výberom — ručné nahratie aj opravený export. */
  async function prevezmiSubor(f: File): Promise<boolean> {
    const predtym = new Set(
      Object.entries(stavy)
        .filter(([, v]) => v.material_id || v.zmazat)
        .map(([k]) => k),
    );
    const a = await nacitaj(
      f,
      delenie,
      zaradenie,
      priradenia(stavy),
      pracovneDosky,
    );
    if (a === null) return false;
    const kluce = [...a.materialy, ...a.hrany, ...a.kovania].map(klucPolozky);
    setPrevzate({
      ostalo: kluce.filter((k) => predtym.has(k)).length,
      nove: kluce.filter((k) => !(k in stavy)).length,
    });
    poslednePoslane.current = JSON.stringify(priradenia(stavy));
    return true;
  }

  /**
   * Opravený export z TopSolidu prišiel do **tohto** konceptu (KOLO 216).
   * User: *„keď mám otvorený import a pošlem ho znova z TopSolidu, chcel
   * som, aby si aktualizoval ten otvorený, nie vytvoril nový"*. Server
   * súbor vymenil; okno ho načíta tou istou cestou ako ručné nahratie
   * opraveného exportu, takže priradenia, delenie a výber ostanú.
   */
  async function prevezmiOpravenyExport(k: Koncept): Promise<void> {
    suborAt.current = k.subor_at ?? null;
    try {
      const f = await stiahniKonceptSubor(k);
      setZostavyMena(k.zostavy_mena ?? null);
      setVykresCesta(k.vykres_cesta ?? null);
      nastavVyber(
        k.zostavy_vybrane && k.zostavy_vybrane.length > 0
          ? {
              urceni: k.zostavy_vybrane,
              celkom: k.zostav_v_zakazke ?? null,
              regal: k.zdroj === "regal",
            }
          : null,
      );
      if (!(await prevezmiSubor(f))) return;
      // Okno nového importu ho už neponúkne — je otvorený tu
      oznacVideny.mutate(k.id);
      toast({
        title: "Prišiel opravený export z TopSolidu",
        description: "Import sa aktualizoval — priradenia a výber ostali.",
      });
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Opravený export sa nepodarilo načítať",
        description: extractErrorMessage(e),
      });
    }
  }

  // Zoznam konceptov sa obnovuje sám (KOLO 215) — iný čas súboru = opravený
  // export toho istého importu z TopSolidu (KOLO 216)
  useEffect(() => {
    if (!konceptId || suborAt.current === null) return;
    const k = koncepty.data?.data.find((x) => x.id === konceptId);
    if (k?.subor_at && k.subor_at !== suborAt.current) {
      void prevezmiOpravenyExport(k);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [koncepty.data, konceptId]);

  /**
   * Založ koncept pre práve nahratý súbor a starý zahoď.
   *
   * Nový riadok, nie prepis: pri autosave sa potom posiela len JSON
   * rozhodnutí a nie zakaždým celý export cez sieť.
   */
  async function odlozKoncept(f: File): Promise<void> {
    const stary = konceptId;
    try {
      const { data: k } = await zalozKoncept.mutateAsync({
        file: f,
        ...stavPreServer(),
        // Opravený export patrí k tomu istému výberu (KOLO 175)
        zostavyVybrane: vyberRef.current,
        zostavVZakazke: vyberZostav?.celkom ?? null,
        zakazkaCasti: cast?.zakazkaId ?? null,
      });
      setKonceptId(k.id);
      suborAt.current = k.subor_at ?? null;
      if (stary) await zmazKoncept.mutateAsync(stary);
    } catch (e) {
      // Import beží ďalej — koncept je poistka, nie podmienka. Ale
      // ticho by znamenalo, že si myslíš, že si chránený, a nie si.
      toast({
        variant: "destructive",
        title: "Rozrobený import sa neuložil",
        description: `${extractErrorMessage(e)} Zatvorenie okna teraz prácu stratí.`,
      });
    }
  }

  /** Vráť sa k rozrobenému importu — aj po vypnutí appky. */
  async function obnov(k: Koncept): Promise<void> {
    let ulozene: {
      stavy?: Record<string, Stav>;
      nazvyHran?: Record<string, string>;
      delenie?: Delenie;
      rotacie?: Record<string, boolean>;
      zaradenie?: Zaradenie;
      pracovneDosky?: PracovneDosky;
      druh?: DruhCisla;
      kVypoctu?: ZakazkaNaPripojenie | null;
    };
    try {
      ulozene = JSON.parse(k.stav_json);
    } catch {
      ulozene = {};
    }
    const stavyZ = ulozene.stavy ?? {};
    const delenieZ = ulozene.delenie ?? {};
    const zaradenieZ = ulozene.zaradenie ?? {};
    // Koncept spred KOLA 168 voľbu nemá — platí dôkaz a hrúbka
    const pracovneDoskyZ = ulozene.pracovneDosky ?? {};
    try {
      const f = await stiahniKonceptSubor(k);
      setStavy(stavyZ);
      setNazvyHran(ulozene.nazvyHran ?? {});
      setDelenie(delenieZ);
      setRotacie(ulozene.rotacie ?? {});
      setZaradenie(zaradenieZ);
      setPracovneDosky(pracovneDoskyZ);
      setCislo(k.cislo_zakazky);
      // Druh z Tandem Zákazky (bez_cisla_typ), inak z uloženého sprievodcu
      setDruh(
        k.bez_cisla_typ === "neskor"
          ? "vypocet"
          : k.bez_cisla_typ === "z"
            ? "mala"
            : (ulozene.druh ?? "zakazka"),
      );
      setVykresCesta(k.vykres_cesta ?? null);
      // Výber z okna vyhrá; inak čo si user zvolil predtým
      setKVypoctu((p) => p ?? ulozene.kVypoctu ?? null);
      setNazov(k.nazov);
      setKonceptId(k.id);
      suborAt.current = k.subor_at ?? null;
      setZostavyMena(k.zostavy_mena ?? null);
      nastavVyber(
        k.zostavy_vybrane && k.zostavy_vybrane.length > 0
          ? {
              urceni: k.zostavy_vybrane,
              celkom: k.zostav_v_zakazke ?? null,
              regal: k.zdroj === "regal",
            }
          : null,
      );
      setPrevzate(null);
      // Analýza nižšie ide s týmito priradeniami — prvá zmena po otvorení
      // ju musí spustiť znova. S `null` ju efekt len zapísal a neposlal
      // (KOLO 217: prvý materiál po otvorení konceptu z Tandem Zostáv
      // neprepočítal hrany ani hrúbku)
      poslednePoslane.current = JSON.stringify(priradenia(stavyZ));
      onOpenChange(true);
      const a = await nacitaj(
        f,
        delenieZ,
        zaradenieZ,
        priradenia(stavyZ),
        pracovneDoskyZ,
      );
      if (a !== null) setKrok(k.krok);
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Rozrobený import sa nepodarilo otvoriť",
        description: extractErrorMessage(e),
      });
    }
  }

  // Odkaz z Tandem Zostáv. Zoznam sa načíta **nanovo**: keď bola appka
  // už otvorená, cache pozná len koncepty spred odoslania z programu.
  useEffect(() => {
    if (!konceptNaOtvorenie) return;
    let zruseny = false;
    void (async () => {
      const r = await koncepty.refetch();
      if (zruseny) return;
      onKonceptOtvoreny?.();
      const k = r.data?.data.find((x) => x.id === konceptNaOtvorenie);
      if (k && k.id === konceptId) {
        // Odkaz na koncept, ktorý je v sprievodcovi (opravený export, KOLO
        // 216) — neobnovovať zo servera, rozpísané by sa stratilo
        onOpenChange(true);
        if (k.subor_at && k.subor_at !== suborAt.current) {
          await prevezmiOpravenyExport(k);
        }
      } else if (k) {
        await obnov(k);
      } else {
        toast({
          variant: "destructive",
          title: "Rozrobený import sa nenašiel",
          description:
            "Mohol byť už naimportovaný alebo zahodený. Pošli kusovník z programu znova.",
        });
      }
    })();
    return () => {
      zruseny = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [konceptNaOtvorenie]);

  /** Zahodenie — zmaže aj koncept na serveri, inak by sa vrátil. */
  async function zahod(): Promise<void> {
    if (konceptId) {
      try {
        await zmazKoncept.mutateAsync(konceptId);
      } catch {
        // Zmazať sa nepodarilo — zoznam ho po 30 dňoch upratá sám.
      }
    }
    setPytaSa(false);
    onOpenChange(false);
    reset();
  }

  // Priradenie dosky určuje dekor, a teda aj hranu — po jeho zmene sa
  // musí analýza prepočítať na serveri (`EdgeAutoMapper` je backendový).
  // Debounce, aby výber v desiatich riadkoch neznamenal desať uploadov.
  const priradeniaSerial = JSON.stringify(priradenia(stavy));
  const priradeniaDebounced = useDebounce(priradeniaSerial, 600);
  useEffect(() => {
    if (!file || !data) return;
    if (poslednePoslane.current === null) {
      poslednePoslane.current = priradeniaDebounced;
      return;
    }
    if (poslednePoslane.current === priradeniaDebounced) return;
    poslednePoslane.current = priradeniaDebounced;
    void nacitaj(
      file,
      delenie,
      zaradenie,
      JSON.parse(priradeniaDebounced),
      pracovneDosky,
    );
    // `nacitaj` aj voľby sú stabilné v rámci jedného otvorenia dialógu
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priradeniaDebounced]);

  // Autosave rozrobeného importu. Ten istý debounce (600 ms) ako pri
  // prepočte analýzy — bez neho by výber v desiatich riadkoch znamenal
  // desať zápisov. Posiela sa **len JSON rozhodnutí**, súbor v koncepte
  // už leží.
  const konceptSnapshot = JSON.stringify({
    cislo,
    nazov,
    krok,
    stavy,
    nazvyHran,
    delenie,
    rotacie,
    zaradenie,
    pracovneDosky,
    kVypoctu,
  });
  const konceptDebounced = useDebounce(konceptSnapshot, 600);
  useEffect(() => {
    if (!konceptId) return;
    ulozKoncept.mutate({ id: konceptId, ...stavPreServer() });
    // Mutácia ani `stavPreServer` nie sú stabilné referencie; spúšťa to
    // výhradne zmena samotného snapshotu.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [konceptDebounced, konceptId]);

  function zmenRotaciu(kluc: string, v: boolean | undefined): void {
    setRotacie((prev) => {
      const dalsie = { ...prev };
      if (v === undefined) delete dalsie[kluc];
      else dalsie[kluc] = v;
      return dalsie;
    });
  }

  /**
   * Zmena delenia pre jednu hrúbku — otázky na materiál sa prepočítajú.
   *
   * `undefined` kľúč odstráni, takže znova platí pravidlo z Nastavení.
   */
  function zmenDelenie(
    hrubka: string,
    vrstvy: number[] | null | undefined,
  ): void {
    const dalsie = { ...delenie };
    if (vrstvy === undefined) delete dalsie[hrubka];
    else dalsie[hrubka] = vrstvy;
    setDelenie(dalsie);
    if (file) {
      void nacitaj(file, dalsie, zaradenie, priradenia(stavy), pracovneDosky);
    }
  }

  /**
   * „Toto je kovanie“ — položka sa presunie a otázky sa prepočítajú.
   *
   * Kľúč je **token materiálu**, ktorý server vracia už normalizovaný,
   * takže sa tu nič normalizovať nemusí.
   */
  function zmenZaradenie(token: string, typ: "dielec" | "kovanie"): void {
    const dalsie = { ...zaradenie, [token]: typ };
    setZaradenie(dalsie);
    if (file) {
      void nacitaj(file, delenie, dalsie, priradenia(stavy), pracovneDosky);
    }
  }

  /**
   * „Je to pracovná / plošná doska" (KOLO 168) — riadok prejde do druhého
   * zoznamu a návrhy sa prepočítajú z druhého cenníka.
   *
   * 38 mm nemusí byť pracovná doska (škárovka 38) a pracovná doska nemusí
   * mať 38 mm; dôkaz z vybranej dosky server vie, ručnú voľbu nie. Doska
   * vybraná v pôvodnom zozname je z cenníka toho druhu, k novému nesedí
   * — zruší sa, inak by 38 mm škárovka dostala PD tyč na bežné metre.
   */
  function zmenDruhDosky(p: MapovaniePolozka): void {
    // Kľúč ako `kluc_priradenia` na serveri (a `priradenia()` tu)
    const dalsie = {
      ...pracovneDosky,
      [`${p.token}|${p.kontext}`]: !p.je_pracovna_doska,
    };
    setPracovneDosky(dalsie);
    const k = klucPolozky(p);
    const s = stavy[k];
    const stavyDalsie = s?.material_id
      ? { ...stavy, [k]: { ...s, material_id: null } }
      : stavy;
    if (stavyDalsie !== stavy) setStavy(stavyDalsie);
    const materialy = priradenia(stavyDalsie);
    // Prepočet ide hneď — debounce priradení by ho inak spustil druhýkrát
    poslednePoslane.current = JSON.stringify(materialy);
    if (file) void nacitaj(file, delenie, zaradenie, materialy, dalsie);
  }

  /** Rozhodnutia pre server — spoločné pre import aj update. */
  function rozhodnutiaPreServer(): Rozhodnutie[] {
    return vsetky.map((p) => {
      const s = stavy[klucPolozky(p)] ?? PRAZDNY;
      return {
        druh: p.druh,
        token: p.token,
        kontext: p.kontext,
        material_id: s.material_id,
        zmazat: s.zmazat,
        zapamatat: s.zapamatat,
      };
    });
  }

  /** „Zobraziť zmeny" — náhľad nič nezapisuje, dá sa opakovať. */
  async function zobrazZmeny(): Promise<void> {
    if (!file || !update) return;
    setNahlad(null);
    setZmenyOtvorene(true);
    try {
      const data = await nahladMut.mutateAsync({
        file,
        rozhodnutia: { rozhodnutia: rozhodnutiaPreServer() },
        delenie,
        zaradenie,
        materialy: priradenia(stavy),
        pracovneDosky,
        zostavyMena,
        zostavyVybrane: vyberRef.current,
      });
      setNahlad(data);
    } catch (e) {
      setZmenyOtvorene(false);
      toast({
        variant: "destructive",
        title: "Zmeny sa nepodarilo spočítať",
        description: extractErrorMessage(e),
      });
    }
  }

  async function aktualizuj(prepisat: string[], kovanie: KovanieVolby): Promise<void> {
    if (!file || !update) return;
    try {
      const v = await vykonatMut.mutateAsync({
        file,
        rozhodnutia: { rozhodnutia: rozhodnutiaPreServer() },
        delenie,
        zaradenie,
        materialy: priradenia(stavy),
        pracovneDosky,
        zostavyMena,
        zostavyVybrane: vyberRef.current,
        prepisat,
        kovanie,
      });
      toast({
        title: druhAktualizovany(nahlad?.co),
        description:
          `Zmenených ${v.zmenenych}, pribudlo ${v.pridanych}, zmizlo ${v.zmazanych}` +
          (v.premenovanych ? `, premenovaných zostáv ${v.premenovanych}.` : "."),
      });
      setZmenyOtvorene(false);
      if (konceptId) {
        try {
          await zmazKoncept.mutateAsync(konceptId);
        } catch {
          // Výpočet je aktualizovaný; osirelý koncept upratá lehota
        }
      }
      onOpenChange(false);
      reset();
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Aktualizácia neprešla",
        description: extractErrorMessage(e),
      });
    }
  }

  /** Export ako nová časť existujúcej zákazky (KOLO 203). */
  async function pridajCast(): Promise<void> {
    if (!file || !data || !cast) return;
    try {
      const { data: v } = await pridatCast.mutateAsync({
        file,
        castNazov: cast.nazov,
        doterajsiaNazov: cast.doterajsiaNazov,
        rozhodnutia: rozhodnutiaImportu(),
        delenie,
        zaradenie,
        materialy: priradenia(stavy),
        pracovneDosky,
        zostavyMena,
        zostavyVybrane: vyberRef.current,
        rotacie: rotacieImportu(),
      });
      toast({
        title: `Časť ${cast.nazov} pridaná`,
        description: `${v.zostav} zostáv, ${v.dielcov} dielcov, ${v.kovani} kovaní — doterajšie časti sa nezmenili.`,
      });
      if (konceptId) {
        try {
          await zmazKoncept.mutateAsync(konceptId);
        } catch {
          // Časť je pridaná; osirelý koncept upratá 30-dňová lehota
        }
      }
      onImported?.(v.zakazka_id, v.varianta_id);
      onOpenChange(false);
      reset();
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Časť sa nepridala",
        description: extractErrorMessage(e),
      });
    }
  }

  function rozhodnutiaImportu(): Rozhodnutie[] {
    return vsetky.map((p) => {
      const s = stavy[klucPolozky(p)] ?? PRAZDNY;
      return {
        druh: p.druh,
        token: p.token,
        kontext: p.kontext,
        material_id: s.material_id,
        zmazat: s.zmazat,
        zapamatat: s.zapamatat,
      };
    });
  }

  function rotacieImportu(): { zostava_kod: string; kod: string; rotovat: boolean }[] {
    return Object.entries(rotacie).map(([k, rotovat]) => {
      const [zostava_kod, kod] = rozlozKlucDielca(k);
      return { zostava_kod, kod, rotovat };
    });
  }

  async function importuj(): Promise<void> {
    if (!file || !data) return;
    const rozhodnutia: Rozhodnutie[] = vsetky.map((p) => {
      const s = stavy[klucPolozky(p)] ?? PRAZDNY;
      return {
        druh: p.druh,
        token: p.token,
        kontext: p.kontext,
        material_id: s.material_id,
        zmazat: s.zmazat,
        zapamatat: s.zapamatat,
      };
    });
    try {
      const { data: v } = await potvrdit.mutateAsync({
        file,
        // Výpočet číslo nemá, malá zákazka má len Z pridelené Rozpočtom
        cislo_zakazky: druh === "vypocet" || (druh === "mala" && !zZRozpoctu) ? "" : cislo.trim(),
        bezCislaTyp: druh === "vypocet" ? "neskor" : druh === "mala" ? "z" : null,
        vykresCesta,
        // Výroba k výpočtu (KOLO 208) — výpočet sa k výpočtu nepripája
        kVypoctuId: druh !== "vypocet" ? (kVypoctu?.id ?? null) : null,
        nazov: nazov.trim() || undefined,
        rozhodnutia,
        delenie,
        zaradenie,
        materialy: priradenia(stavy),
        pracovneDosky,
        zostavyMena,
        zostavyVybrane: vyberRef.current,
        rotacie: Object.entries(rotacie).map(([k, rotovat]) => {
          const [zostava_kod, kod] = rozlozKlucDielca(k);
          return { zostava_kod, kod, rotovat };
        }),
      });
      toast({
        title:
          druh !== "vypocet" && kVypoctu
            ? `Výroba pripojená k výpočtu ${kVypoctu.cislo_zakazky}`
            : "Zákazka naimportovaná",
        description: `${v.zostav} zostáv, ${v.dielcov} dielcov, ${v.kovani} kovaní${
          v.setov ? `, z toho ${v.setov} setov` : ""
        }.`,
      });
      if (konceptId) {
        try {
          await zmazKoncept.mutateAsync(konceptId);
        } catch {
          // Zákazka je vytvorená; osirelý koncept upratá 30-dňová lehota
        }
      }
      onImported?.(v.zakazka_id, druh !== "vypocet" && kVypoctu ? v.varianta_id : undefined);
      onOpenChange(false);
      reset();
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Import neprešiel",
        description: extractErrorMessage(e),
      });
    }
  }

  // Čo ukázať v plávajúcom pruhu: prednosť má to, čo je práve
  // v pamäti (minimalizované okno), inak najnovší koncept zo servera —
  // ten prežil aj vypnutie appky.
  const zoServera = koncepty.data?.data[0] ?? null;
  const rozrobene =
    data && file
      ? {
          cislo: cislo.trim(),
          nazovSuboru: file.name,
          krok,
          koncept: null as Koncept | null,
        }
      : zoServera
        ? {
            cislo: zoServera.cislo_zakazky,
            nazovSuboru: zoServera.nazov_suboru,
            krok: zoServera.krok,
            koncept: zoServera,
          }
        : null;

  function zatvorZDialogu(): void {
    // Kým nie je čo stratiť, zatvára sa bez pýtania.
    if (!data) {
      onOpenChange(false);
      reset();
      return;
    }
    setPytaSa(true);
  }

  function minimalizuj(): void {
    setPytaSa(false);
    onOpenChange(false);
  }

  return (
    <>
      {!open && rozrobene ? (
        <div
          // Na mobile cez celú šírku pri spodku (KOLO 212) — pri `right-4`
          // bez ľavého okraja mal pás 410 px a 36 px vľavo bolo mimo obrazovky.
          className="fixed inset-x-2 bottom-2 z-40 flex items-center gap-2 rounded-lg border bg-background p-2 shadow-dialog sm:inset-x-auto sm:bottom-4 sm:right-4 sm:gap-3 sm:p-3"
          data-testid="rozrobeny-import"
        >
          <FileUp className="hidden h-5 w-5 shrink-0 text-primary sm:block" />
          <div className="min-w-0 flex-1 text-sm leading-tight">
            <div className="truncate font-medium">
              {rozrobene.koncept?.zdroj === "regal"
                ? "Regál CRC — diely Tandemu na nacenenie"
                : (rozrobene.koncept?.cast_nova ?? Boolean(cast))
                ? "Rozrobená nová časť"
                : (rozrobene.koncept?.rezim ?? (update ? "update" : "novy")) === "update"
                  ? "Rozrobená aktualizácia"
                  : "Rozrobený import"}
              {rozrobene.cislo ? ` — ${rozrobene.cislo}` : ""}
            </div>
            <div className="truncate text-xs text-muted-foreground">
              {rozrobene.nazovSuboru} · krok{" "}
              {KROKY.findIndex((x) => x.kluc === rozrobene.krok) + 1} z{" "}
              {KROKY.length}
            </div>
          </div>
          <Button
            size="sm"
            onClick={() => {
              const k = rozrobene.koncept;
              if (!k) onOpenChange(true);
              else if (onVyberKoncept) onVyberKoncept(k);
              else void obnov(k);
            }}
          >
            Pokračovať
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              if (rozrobene.koncept) {
                void zmazKoncept.mutateAsync(rozrobene.koncept.id);
                return;
              }
              void zahod();
            }}
          >
            Zahodiť
          </Button>
        </div>
      ) : null}

      <Dialog
        open={open}
        onOpenChange={(o) => {
          if (o) {
            onOpenChange(true);
            return;
          }
          zatvorZDialogu();
        }}
      >
        <DialogContent
          className="flex max-h-[85vh] w-[96vw] max-w-5xl flex-col"
          // Klik vedľa dialógu a Escape sú presne tie dva spôsoby, akými
          // sa import zatváral omylom. Kým nie je čo stratiť, fungujú
          // ako predtým; s načítanou analýzou nerobia nič a zatvoriť sa
          // dá len cez „×" alebo „Zrušiť", ktoré sa pýtajú.
          onInteractOutside={(e) => {
            if (data) e.preventDefault();
          }}
          onEscapeKeyDown={(e) => {
            if (data) e.preventDefault();
          }}
        >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileUp className="h-5 w-5 text-primary" />
            {cast
              ? `Výroba — ${cast.nazov}`
              : vyberZostav?.regal
                ? "Regál CRC — diely Tandemu na nacenenie"
                : update
                  ? "Aktualizácia z opraveného exportu"
                  : "Import z TopSolidu"}
            {data ? (
              <Button
                variant="ghost"
                size="sm"
                className="absolute right-12 top-3 h-8 px-2"
                onClick={minimalizuj}
                title="Odlož okno a choď robiť niečo iné — import zostane"
              >
                <Minus className="h-4 w-4" />
                <span className="sr-only">Minimalizovať</span>
              </Button>
            ) : null}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {cast
              ? "Export výroby do existujúcej zákazky. Mapovanie je ako pri novom importe; výpočty ani doterajšie časti sa nezmenia."
              : vyberZostav?.regal
              ? "Diely, ktoré k regálu CRC vyrába Tandem. Priraď materiály a hrany; na konci uvidíš, čo pribudne do nábytku."
              : update
              ? "Nahraj opravený export z TopSolidu. Mapovanie je predvyplnené z pamäte; na konci uvidíš, čo sa zmení."
              : "Nahraj surový export z TopSolidu a prirad názvy materiálov, hrán a kovaní na položky cenníka."}
          </DialogDescription>
        </DialogHeader>

        {vyberZostav ? <PasVyberu vyber={vyberZostav} /> : null}
        {cast ? (
          <p className="rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-xs">
            Pridáva sa <span className="font-medium">výroba „{cast.nazov}“</span>
            {cislo ? ` do zákazky ${cislo}` : ""}
            {cast.doterajsiaNazov
              ? ` — doterajšia výroba bude časť „${cast.doterajsiaNazov}“`
              : ""}
            . Výpočty ani dielce doterajších častí sa nezmenia, výroba spolu bude súčet častí.
          </p>
        ) : null}

        <div className="grid gap-3 md:grid-cols-3">
          <div>
            <Label htmlFor="ts-subor" className="flex items-center gap-1">
              {data ? (
                <>
                  <Upload className="h-3.5 w-3.5" />
                  Nahrať opravený export
                </>
              ) : (
                "Export z TopSolidu"
              )}
            </Label>
            <Input
              id="ts-subor"
              type="file"
              accept=".xlsm,.xlsx,.csv,.txt"
              className="h-9"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void nahraj(f);
              }}
            />
            {/* Bez tejto vety sa po oprave v TopSolide nedá poznať, či
                priradenia prežili — a celý zmysel znovu-nahratia je,
                že prežijú. */}
            {file ? (
              <p className="mt-1 text-xs text-muted-foreground">
                Nahraté: {file.name}
                {prevzate
                  ? ` · prevzatých priradení: ${prevzate.ostalo}` +
                    (prevzate.nove ? ` · nových položiek: ${prevzate.nove}` : "")
                  : ""}
              </p>
            ) : null}
          </div>
          <div className={update || cast ? "hidden" : undefined}>
            <Label htmlFor="ts-cislo">Číslo zákazky</Label>
            <div className="flex gap-1.5">
              <select
                aria-label="Druh čísla"
                value={druh}
                onChange={(e) => setDruh(e.target.value as DruhCisla)}
                // Z pridelené Rozpočtom je vo výkrese — druh sa už nemení
                disabled={zZRozpoctu}
                className="h-9 shrink-0 rounded-md border border-input bg-background px-2 text-sm"
                title={DRUHY_CISLA.find((d) => d.druh === druh)?.popis}
              >
                {DRUHY_CISLA.map((d) => (
                  <option key={d.druh} value={d.druh}>
                    {d.nazov}
                  </option>
                ))}
              </select>
              {druh === "zakazka" ? (
                <Input
                  id="ts-cislo"
                  className="h-9"
                  value={cislo}
                  onChange={(e) => setCislo(e.target.value)}
                  // Povinné pole — nech je vidieť, kam ísť, keď pätička
                  // hlási, že číslo chýba
                  aria-invalid={Boolean(data) && !cislo.trim()}
                  placeholder="napr. AA6319"
                />
              ) : (
                <Input
                  id="ts-cislo"
                  className="h-9 bg-muted"
                  readOnly
                  value={zZRozpoctu ? cislo : ""}
                  // Pole je úzke — celá veta je v title
                  placeholder={druh === "vypocet" ? "V… pri importe" : "Z… pri importe"}
                  title={
                    zZRozpoctu
                      ? "Číslo Z pridelil Rozpočet pri poslaní z Tandem Zákazky — je vo výkrese"
                      : `Číslo ${druh === "vypocet" ? "V" : "Z"}… pridelí Rozpočet pri importe`
                  }
                />
              )}
            </div>
          </div>
          {!update && !cast && druh !== "vypocet" ? (
            <div className="sm:col-span-2" data-testid="k-vypoctu">
              <Label>Patrí k výpočtu</Label>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                {kVypoctu ? (
                  <span className="inline-flex items-center gap-1 rounded-full border border-primary bg-primary/10 px-2.5 py-1 text-xs">
                    <span className="font-mono">{kVypoctu.cislo_zakazky}</span>
                    {kVypoctu.nazov ?? ""}
                    <button
                      type="button"
                      className="ml-1 text-muted-foreground hover:text-foreground"
                      aria-label="Bez výpočtu"
                      onClick={() => setKVypoctu(null)}
                    >
                      ✕
                    </button>
                  </span>
                ) : (
                  <span className="text-xs text-muted-foreground">
                    žiadny — vznikne nová zákazka s výrobou
                  </span>
                )}
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 px-2 text-xs"
                  onClick={() => setVyberVypoctu((x) => !x)}
                >
                  {vyberVypoctu ? "Zavrieť" : kVypoctu ? "Zmeniť" : "Vybrať výpočet…"}
                </Button>
              </div>
              {vyberVypoctu ? (
                <div className="mt-2">
                  <VyberZakazky
                    druh="vypocet"
                    okrem=""
                    onVyber={(z) => {
                      setKVypoctu(z);
                      setVyberVypoctu(false);
                    }}
                  />
                </div>
              ) : null}
              <p className="mt-1 text-[11px] text-muted-foreground">
                Výroba pribudne do výpočtu ako záložka — výpočty ostanú, zákazka
                dostane číslo z exportu.
              </p>
            </div>
          ) : null}
          <div className={update || cast ? "hidden" : undefined}>
            <Label htmlFor="ts-nazov">Názov</Label>
            <Input
              id="ts-nazov"
              className="h-9"
              value={nazov}
              onChange={(e) => setNazov(e.target.value)}
              placeholder="napr. Pekáreň Maan Nitra"
            />
          </div>
        </div>

        {analyza.isPending ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            Čítam export…
          </p>
        ) : null}

        {data ? (
          <>
            {/* Jeden riadok štatistík — čo v exporte je a čo z neho
                vznikne. Návod („priraď názvy") tu bol tri riadky textu,
                ktoré si po prvom importe nikto nečíta; podstatné je,
                kde sa opravuje zle naučené priradenie. */}
            <p className="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
              <span className="font-medium text-foreground">
                {data.zostav} zostáv
              </span>
              <span>·</span>
              <span>
                {data.dielcov} riadkov
                {data.dielcov_po_importe !== data.dielcov
                  ? ` → ${data.dielcov_po_importe} dielcov`
                  : ""}
              </span>
              <span>·</span>
              <span>{data.kovani} kovaní</span>
              {/* Odkaz je `<a>`, nie `<Link>` — dialóg sa renderuje aj
                  v testoch mimo routera a zle priradenú pamäť treba vedieť
                  nájsť odtiaľto, nie ju hľadať v Nastaveniach */}
              <a
                href={odkazNaNastavenia("topsolid")}
                className="ml-auto underline underline-offset-2"
                target="_blank"
                rel="noreferrer"
                title="Zle zapamätané priradenie sa dá prezrieť a zmazať v Nastaveniach"
              >
                Zapamätané priradenia
              </a>
            </p>

            <PasKrokov
              krok={krok}
              chybaMaterialov={chybaKroku2.length}
              chybaKovani={chybaKovani.length}
              nalezy={nalezyPreKrok}
              onKrok={setKrok}
            />

            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
              {krok === "dielce" ? (
                <>
                  {/* Len nálezy tohto kroku (KOLO 114) — hlásenie o hrane
                      na obrazovke o delení dielcov sa nedá opraviť
                      a prekrylo by to, čo tu naozaj treba vidieť.
                      Úplný zoznam je v poslednom kroku. */}
                  <OpravyZoznam opravy={opravyPreKrok(data.opravy, "dielce")} />

                  {/* Čo import s dielcami spraví — kým sa to nestalo */}
                  <TabulkaDelenia
                    dielce={data.dielce}
                    delenie={delenie}
                    onDelenie={zmenDelenie}
                    prepocitava={analyza.isPending}
                  />

                  {/* Čo appka mení oproti exportu pri holej doske (KOLO 173) */}
                  <TabulkaOdcitaniaHran dielce={data.dielce} />

                  <TabulkaCnc
                    dielce={data.dielce}
                    rotacie={rotacie}
                    onRotacia={zmenRotaciu}
                  />
                </>
              ) : null}

              {krok === "materialy" ? (
                <>
                  <OpravyZoznam
                    opravy={opravyPreKrok(data.opravy, "materialy")}
                  />
                  <VysvetlenieZapamatania />
                  <TabulkaPoloziek
                    nadpis="Plošné materiály"
                    polozky={data.materialy.filter(
                      (p) => !p.je_pracovna_doska,
                    )}
                    stavy={stavy}
                    nazvyVybranych={nazvyHran}
                    filterPre={(p) => filterPreDruh(p, p.je_pracovna_doska)}
                    predvyplneniePre={predvyplneniePolozky}
                    onZmena={zmenStav}
                    onKovanie={(p) => zmenZaradenie(p.token, "kovanie")}
                    onPrepniDosku={zmenDruhDosky}
                    menaZostav={zostavyMena}
                  />
                  <TabulkaPoloziek
                    nadpis="Pracovné dosky"
                    poznamka="kupujú sa hotové na bežné metre"
                    polozky={data.materialy.filter((p) => p.je_pracovna_doska)}
                    stavy={stavy}
                    nazvyVybranych={nazvyHran}
                    filterPre={(p) => filterPreDruh(p, p.je_pracovna_doska)}
                    predvyplneniePre={predvyplneniePolozky}
                    onZmena={zmenStav}
                    onKovanie={(p) => zmenZaradenie(p.token, "kovanie")}
                    onPrepniDosku={zmenDruhDosky}
                    menaZostav={zostavyMena}
                  />
                </>
              ) : null}

              {krok === "materialy" ? (
                data.hrany.length ? (
                  <section className="rounded-lg border">
                    <h3 className="flex flex-wrap items-center gap-2 border-b bg-background-alt px-3 py-1.5 text-sm font-medium">
                      Hrany
                      <span className="text-xs font-normal text-muted-foreground">
                        ({data.hrany.length})
                      </span>
                      <span className="text-xs font-normal text-muted-foreground">
                        podľa dekoru priradenej dosky — klik na hranu ju zmení
                      </span>
                      {analyza.isPending ? (
                        <span className="text-xs font-normal text-muted-foreground">
                          prepočítavam…
                        </span>
                      ) : null}
                    </h3>
                    <div className="space-y-3 p-3">
                      <TabulkaHran
                        hrany={data.hrany}
                        stavy={stavy}
                        nazvyVybranych={nazvyHran}
                        onZmena={zmenStav}
                        doskaPreToken={doskaPreToken}
                        predvyplneniePre={predvyplneniePolozky}
                      />
                    </div>
                  </section>
                ) : (
                  <p className="py-6 text-center text-sm text-muted-foreground">
                    Export nemá na dielcoch žiadne hrany.
                  </p>
                )
              ) : null}

              {krok === "kovania" ? (
                <>
                  <VysvetlenieZapamatania />
                  <TabulkaPoloziek
                    nadpis="Kovania"
                    polozky={data.kovania}
                    stavy={stavy}
                    nazvyVybranych={nazvyHran}
                    filterPre={(p) => filterPreDruh(p, false)}
                    predvyplneniePre={predvyplneniePolozky}
                    onZmena={zmenStav}
                    menaZostav={zostavyMena}
                  />

                  {/* Úplný zoznam so všetkými nálezmi — posledný krok je
                      prirodzené miesto „pred potvrdením si pozri, čo
                      opraviť v modeli" a hlavne: `OpravyZoznam` má
                      tlačidlo na skopírovanie, ktorým sa nálezy posielajú
                      konštruktérovi. Rozsypaním po krokoch by sa to
                      rozbilo. Padne sem aj nález neznámeho druhu. */}
                  <OpravyZoznam
                    opravy={data.opravy}
                    nadpis="Všetky nálezy na opravu v TopSolide"
                  />
                </>
              ) : null}
            </div>
          </>
        ) : null}

        <DialogFooter className="flex-col items-stretch gap-2 sm:flex-row sm:items-center">
          {/* Nie iba vypnuté tlačidlo — bez tejto vety by user hľadal,
              čo mu chýba, medzi stovkou riadkov */}
          {data && krok === "materialy" && chybaKroku2.length > 0 ? (
            <p className="mr-auto text-xs text-destructive">
              Nepriradených položiek: {chybaKroku2.length} (napr.{" "}
              {chybaKroku2[0]?.popis}). Bez priradenej dosky sa dekor
              hrany odvodiť nedá.
            </p>
          ) : null}
          {data && krok === "kovania" && nevyriesene.length > 0 ? (
            <p className="mr-auto text-xs text-destructive">
              Nepriradených položiek: {nevyriesene.length} (napr.{" "}
              {nevyriesene[0]?.popis})
            </p>
          ) : null}
          {/* Tretí dôvod, prečo je „Importovať" vypnuté — do KOLA 125
              jediný bez vety. User priradil všetko a hľadal chybu medzi
              kovaniami, pritom mu chýbalo číslo zákazky (šedý text
              v poli je placeholder, nie hodnota). */}
          {data &&
          krok === "kovania" &&
          nevyriesene.length === 0 &&
          !cisloOk ? (
            <p className="mr-auto text-xs text-destructive">
              Vyplň číslo zákazky, alebo vyber Výpočet či Malú zákazku bez AA —
              inak sa import nedá dokončiť.
            </p>
          ) : null}

          <Button variant="outline" onClick={zatvorZDialogu}>
            Zrušiť
          </Button>

          {data && krok !== "dielce" ? (
            <Button
              variant="outline"
              onClick={() =>
                setKrok(krok === "kovania" ? "materialy" : "dielce")
              }
            >
              Späť
            </Button>
          ) : null}

          {data && krok !== "kovania" ? (
            <Button
              disabled={krok === "materialy" && chybaKroku2.length > 0}
              onClick={() =>
                setKrok(krok === "dielce" ? "materialy" : "kovania")
              }
            >
              Ďalej
            </Button>
          ) : cast ? (
            <Button
              disabled={!file || !data || nevyriesene.length > 0 || pridatCast.isPending}
              onClick={() => void pridajCast()}
            >
              {pridatCast.isPending ? "Pridávam…" : "Pridať časť"}
            </Button>
          ) : update ? (
            <Button
              disabled={!file || !data || nevyriesene.length > 0}
              onClick={() => void zobrazZmeny()}
            >
              Zobraziť zmeny
            </Button>
          ) : (
            <Button
              disabled={
                !file ||
                !data ||
                !cisloOk ||
                nevyriesene.length > 0 ||
                potvrdit.isPending
              }
              onClick={() => void importuj()}
            >
              {potvrdit.isPending ? "Importujem…" : "Importovať"}
            </Button>
          )}
        </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Zatvorenie sa pýta — user: „sprav to tak aby som to nevedel
          ľahko vypnúť… lebo sa mi stáva, že ho omylom vypnem." Sú to
          tri odpovede, nie dve: „zavrieť" v tomto okne znamená raz
          odložiť a raz zahodiť a to sa nesmie skrývať za jedno
          tlačidlo. */}
      <Dialog open={pytaSa} onOpenChange={setPytaSa}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Ukončiť import?</DialogTitle>
            <DialogDescription>
              Rozrobený import je uložený na serveri — po minimalizovaní
              sa k nemu vrátiš aj z iného počítača a aj po vypnutí appky.
              „Zahodiť" ho zmaže aj tam.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPytaSa(false)}>
              Pokračovať v importe
            </Button>
            <Button variant="outline" onClick={minimalizuj}>
              Minimalizovať
            </Button>
            <Button variant="destructive" onClick={() => void zahod()}>
              Zahodiť import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {update ? (
        <UpdateZmenyDialog
          open={zmenyOtvorene}
          onOpenChange={setZmenyOtvorene}
          nahlad={nahlad}
          bezi={vykonatMut.isPending}
          onPotvrd={(prepisat, kovanie) => void aktualizuj(prepisat, kovanie)}
        />
      ) : null}
    </>
  );
}
